// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Contador/ContadorCliente.tsx
//  Um cliente no Portal do Contador: documentos da competência (filtro, XML, DANFE,
//  ZIP do mês) e a conferência (numeração faltante, canceladas, inutilizações).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, FileCode, FileText, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { EnviarXmlsBotao } from '@/features/cofre/EnviarXmlsBotao';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { AnaliseFiscalPainel } from '@/features/cofre/AnaliseFiscalPainel';
import {
  competenciaAtual, contadorService, formatarDoc, MODELOS_FILTRO, nomeModelo, ROTULO_SITUACAO, rotuloCompetencia,
  SENTIDO, SITUACAO, ultimasCompetencias, type DocumentoLinha, type Sentido, type Situacao,
} from '@/features/contador/contadorService';

const TODOS = '__todos__';
const TAMANHO_PAGINA = 50;

export default function ContadorCliente() {
  const { id } = useParams();
  const contribuinteId = Number(id);
  const [params, setParams] = useSearchParams();
  const competencia = Number(params.get('competencia')) || competenciaAtual();
  const mudarCompetencia = (c: number) => setParams({ competencia: String(c) }, { replace: true });

  const { data: carteira } = useQuery({
    queryKey: ['contador', 'clientes', competencia],
    queryFn: () => contadorService.clientes(competencia),
  });
  const cliente = carteira?.clientes.find((c) => c.contribuinteId === contribuinteId);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="min-w-0">
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/contador"><ArrowLeft className="mr-1 h-4 w-4" />Meus clientes</Link>
          </Button>
          <h1 className="text-2xl font-bold text-foreground truncate">{cliente?.razaoSocial ?? 'Cliente'}</h1>
          <p className="text-muted-foreground">{formatarDoc(cliente?.cnpjCpf)}{cliente?.uf ? ` · ${cliente.uf}` : ''}</p>
        </div>
        <Select value={String(competencia)} onValueChange={(v) => mudarCompetencia(Number(v))}>
          <SelectTrigger className="w-48 sm:ml-auto"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ultimasCompetencias().map((c) => (
              <SelectItem key={c} value={String(c)}>{rotuloCompetencia(c)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="documentos">
        <TabsList>
          <TabsTrigger value="documentos">Documentos</TabsTrigger>
          <TabsTrigger value="conferencia">Conferência</TabsTrigger>
          <TabsTrigger value="analises">Análises</TabsTrigger>
        </TabsList>
        <TabsContent value="documentos" className="mt-4">
          <Documentos contribuinteId={contribuinteId} cnpj={cliente?.cnpjCpf ?? String(contribuinteId)} competencia={competencia} />
        </TabsContent>
        <TabsContent value="conferencia" className="mt-4">
          <ConferenciaAba contribuinteId={contribuinteId} competencia={competencia} />
        </TabsContent>
        <TabsContent value="analises" className="mt-4">
          <AnaliseFiscalPainel chave={['contador', contribuinteId]} competencia={competencia}
            carregar={(c, anexo) => contadorService.analises(contribuinteId, c, anexo)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Documentos({ contribuinteId, cnpj, competencia }: { contribuinteId: number; cnpj: string; competencia: number }) {
  const [modelo, setModelo] = useState<number | undefined>();
  const [sentido, setSentido] = useState<Sentido | undefined>();
  const [situacao, setSituacao] = useState<Situacao | undefined>();
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [pagina, setPagina] = useState(1);
  const [baixandoZip, setBaixandoZip] = useState(false);

  const qc = useQueryClient();
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['contador', 'documentos', contribuinteId, competencia, modelo, sentido, situacao, buscaAplicada, pagina],
    queryFn: () => contadorService.documentos(contribuinteId, {
      competencia, modelo, sentido, situacao, busca: buscaAplicada || undefined, pagina, tamanhoPagina: TAMANHO_PAGINA,
    }),
    placeholderData: keepPreviousData,
  });
  const paginas = data ? Math.max(1, Math.ceil(data.total / TAMANHO_PAGINA)) : 1;
  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  const executar = async (acao: () => Promise<void>) => {
    try { await acao(); } catch (e) { toast.error((e as Error).message); }
  };
  const baixarZip = async () => {
    setBaixandoZip(true);
    await executar(() => contadorService.baixarZip(contribuinteId, cnpj, competencia, modelo, sentido));
    setBaixandoZip(false);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={modelo ? String(modelo) : TODOS} onValueChange={(v) => filtrar(setModelo)(v === TODOS ? undefined : Number(v))}>
            <SelectTrigger className="w-auto min-w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os modelos</SelectItem>
              {MODELOS_FILTRO.map((m) => <SelectItem key={m.valor} value={String(m.valor)}>{m.rotulo}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sentido === undefined ? TODOS : String(sentido)}
            onValueChange={(v) => filtrar(setSentido)(v === TODOS ? undefined : (Number(v) as Sentido))}>
            <SelectTrigger className="w-auto min-w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Entradas e saídas</SelectItem>
              <SelectItem value={String(SENTIDO.Saida)}>Saídas</SelectItem>
              <SelectItem value={String(SENTIDO.Entrada)}>Entradas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={situacao === undefined ? TODOS : String(situacao)}
            onValueChange={(v) => filtrar(setSituacao)(v === TODOS ? undefined : (Number(v) as Situacao))}>
            <SelectTrigger className="w-auto min-w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Qualquer situação</SelectItem>
              {Object.values(SITUACAO).map((s) => <SelectItem key={s} value={String(s)}>{ROTULO_SITUACAO[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <form className="relative" onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca.trim()); setPagina(1); }}>
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8 w-56" placeholder="Número, chave, CNPJ ou nome" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </form>
          <div className="ml-auto flex flex-wrap gap-2">
            <EnviarXmlsBotao enviar={(f) => contadorService.enviarXmls(contribuinteId, f)}
              aoConcluir={() => qc.invalidateQueries({ queryKey: ['contador'] })} />
            <Button onClick={baixarZip} disabled={baixandoZip || !data?.total}>
              {baixandoZip ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Baixar XMLs do mês
            </Button>
          </div>
        </div>
        {data && (
          <p className="text-sm text-muted-foreground pt-2">
            {data.total} documento(s) · {formatCurrency(data.valorTotal)} autorizados
            {(modelo || sentido !== undefined) && ' · o ZIP segue o modelo e o sentido escolhidos'}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4 space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
        ) : !data?.itens.length ? (
          <p className="py-10 text-center text-muted-foreground">Nenhum documento nesta competência com esses filtros.</p>
        ) : (
          <div className={`overflow-x-auto ${isFetching ? 'opacity-60' : ''}`}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emissão</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead className="text-right">Número</TableHead>
                  <TableHead>Participante</TableHead>
                  <TableHead>CFOP</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Arquivos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.itens.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="whitespace-nowrap">{new Date(d.dhEmissao).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {nomeModelo(d.modelo)} <span className="text-xs text-muted-foreground">{d.sentido === SENTIDO.Saida ? 'saída' : 'entrada'}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono">{d.numero}{d.serie ? `/${d.serie}` : ''}</TableCell>
                    <TableCell className="max-w-56">
                      <span className="block truncate">{d.participanteNome ?? '—'}</span>
                      {d.participanteCnpjCpf && <span className="block text-xs text-muted-foreground">{formatarDoc(d.participanteCnpjCpf)}</span>}
                    </TableCell>
                    <TableCell>{d.cfopPrincipal ?? '—'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">{formatCurrency(d.valorTotal)}</TableCell>
                    <TableCell><SituacaoBadge s={d.situacao} /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" title="Baixar XML" onClick={() => executar(() => contadorService.baixarXml(d))}>
                        <FileCode className="h-4 w-4" />
                      </Button>
                      {d.modelo === 55 && (
                        <Button variant="ghost" size="icon" title="Abrir DANFE" onClick={() => executar(() => contadorService.abrirDanfe(d))}>
                          <FileText className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {data && paginas > 1 && (
          <div className="flex items-center justify-end gap-2 p-3 border-t text-sm">
            <span className="text-muted-foreground">Página {pagina} de {paginas}</span>
            <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>Próxima</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SituacaoBadge({ s }: { s: DocumentoLinha['situacao'] }) {
  if (s === SITUACAO.Autorizada) return <Badge variant="outline">Autorizada</Badge>;
  return <Badge variant="destructive">{ROTULO_SITUACAO[s]}</Badge>;
}

function ConferenciaAba({ contribuinteId, competencia }: { contribuinteId: number; competencia: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['contador', 'conferencia', contribuinteId, competencia],
    queryFn: () => contadorService.conferencia(contribuinteId, competencia),
  });
  if (isLoading || !data) return <Skeleton className="h-48" />;

  const faltando = data.numeracaoFaltante.reduce((t, f) => t + f.quantidade, 0);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Indicador titulo="Números faltando" valor={faltando} alerta={faltando > 0} />
        <Indicador titulo="Canceladas" valor={data.canceladas} />
        <Indicador titulo="Denegadas" valor={data.denegadas} alerta={data.denegadas > 0} />
        <Indicador titulo="Eventos sem a nota" valor={data.eventosSemDocumento} alerta={data.eventosSemDocumento > 0} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Numeração faltante nas saídas (NF-e e NFC-e)</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.numeracaoFaltante.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">
              Nenhum buraco na numeração desta competência. Números inutilizados na SEFAZ não contam como falta.
            </p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Modelo</TableHead><TableHead>Série</TableHead><TableHead>Números</TableHead><TableHead className="text-right">Quantidade</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.numeracaoFaltante.map((f) => (
                  <TableRow key={`${f.modelo}-${f.serie}-${f.inicio}`}>
                    <TableCell>{nomeModelo(f.modelo)}</TableCell>
                    <TableCell>{f.serie}</TableCell>
                    <TableCell className="font-mono">{f.inicio === f.fim ? f.inicio : `${f.inicio} a ${f.fim}`}</TableCell>
                    <TableCell className="text-right">{f.quantidade}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Inutilizações da competência</CardTitle></CardHeader>
        <CardContent className="p-0">
          {data.inutilizacoes.length === 0 ? (
            <p className="px-6 pb-6 text-sm text-muted-foreground">Nenhuma faixa inutilizada.</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Modelo</TableHead><TableHead>Série</TableHead><TableHead>Faixa</TableHead><TableHead>Protocolo</TableHead>
                <TableHead>Justificativa</TableHead><TableHead className="text-right">XML</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.inutilizacoes.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell>{nomeModelo(i.modelo)}</TableCell>
                    <TableCell>{i.serie}</TableCell>
                    <TableCell className="font-mono">{i.numeroInicial === i.numeroFinal ? i.numeroInicial : `${i.numeroInicial} a ${i.numeroFinal}`}</TableCell>
                    <TableCell className="font-mono text-xs">{i.protocolo ?? '—'}</TableCell>
                    <TableCell className="max-w-64 truncate">{i.justificativa ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" title="Baixar XML"
                        onClick={() => contadorService.baixarInutilizacao(i.id).catch((e: Error) => toast.error(e.message))}>
                        <FileCode className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Indicador({ titulo, valor, alerta = false }: { titulo: string; valor: number; alerta?: boolean }) {
  return (
    <Card className={alerta ? 'border-amber-500' : undefined}>
      <CardContent className="pt-6">
        <p className="text-2xl font-semibold">{valor}</p>
        <p className="text-sm text-muted-foreground">{titulo}</p>
      </CardContent>
    </Card>
  );
}
